import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import '../styles/components/Footer.scss';
import { FaWhatsapp } from 'react-icons/fa';
import logo from '../assets/logo.png';
import wetekup from '../assets/wetekup.png';
import axios from 'axios';

const Footer = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const isContactPage = location.pathname === '/contact';
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Footer form state
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const isMobile = windowWidth < 768;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form fields
    if (!form.name || !form.email || !form.message) {
      setStatus({ type: 'error', message: t('contact.error', 'Veuillez remplir tous les champs.') });
      return;
    }

    setLoading(true);
    setStatus(null);
    
    try {
      const response = await axios.post(`${process.env.REACT_APP_SERVER_URL}/send`, form);
      if (response.data.success) {
        setStatus({ type: 'success', message: t('contact.success', 'Votre message a été envoyé avec succès !') });
        setForm({ name: '', email: '', message: '' });
      } else {
        setStatus({ type: 'error', message: t('contact.error', "Une erreur s'est produite. Veuillez réessayer.") });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setStatus({ 
        type: 'error', 
        message: t('contact.error', "Une erreur s'est produite. Veuillez réessayer.") 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="footer">
      {/* Blue Contact Section */}
      {!isContactPage && (
        <div className="footer-contact-section">
          <h2>{t('footer.contact_title')}</h2>
          {status && (
            <div className={`footer-contact-status ${status.type}`}>
              {status.message}
            </div>
          )}
          <form className="footer-contact-form" onSubmit={handleSubmit}>
            <div className="footer-form-group">
              <input 
                type="text" 
                name="name"
                placeholder={t('footer.form.name_placeholder')} 
                value={form.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="footer-form-group">
              <input 
                type="email" 
                name="email"
                placeholder={t('footer.form.email_placeholder')} 
                value={form.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="footer-form-group">
              <textarea
                name="message"
                placeholder={t('footer.form.message_placeholder')} 
                value={form.message}
                onChange={handleChange}
                required
                rows={3}
              />
            </div>
            <div className="footer-form-actions">
              <button 
                type="submit" 
                className="footer-contact-btn" 
                disabled={loading || !form.name || !form.email || !form.message}
              >
                {loading ? t('contact.sending', 'Envoi...') : t('footer.form.submit')}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Blue Band for Contact Page */}
      {isContactPage && (
        <div className="footer-blue-band"></div>
      )}
      
      {/* White Footer Section */}
      <div className="footer-main">
        <div className="footer-col logo-col">
          <img src={logo} alt="Powerline Logo" className="footer-logo" />
        </div>
        
        <div className="footer-col contact-col">
          <div className="footer-heading">{t('footer.coordinates')}</div>
          <div className="footer-contact">
            <a 
              href="https://wa.me/33754324420" 
              target="_blank" 
              rel="noopener noreferrer"
              className="whatsapp-link"
            >
              <FaWhatsapp size={20} style={{ marginRight: '2px' }} />
              +33 754324420
            </a>
          </div>

          <div className="footer-contact">dani.powerline@gmail.com</div>
        </div>
        
        <div className="footer-col support-col">
          <div className="footer-heading">{t('footer.support')}</div>
          <div className="footer-link">{t('footer.terms')}</div>
          <div className="footer-link">{t('footer.privacy')}</div>
        </div>
        
        <div className="footer-col guarantee-col">
          <div className="footer-guarantee">
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="#5ea1fa" strokeWidth="2"/>
              <path d="M12 6v6l4 2" stroke="#5ea1fa" strokeWidth="2"/>
            </svg>
            <span>{t('footer.guarantee')}</span>
          </div>
        </div>
      </div>
      <hr />
      <div className='wetekup'>
        <img src={wetekup} alt="WeTekup Logo" />
        <span>
          <Trans i18nKey="footer.wetekup"
            components={{ 1: <a href="https://www.instagram.com/we_tekup/" target="_blank" rel="noopener noreferrer">WE TEKUP</a> }}
            values={{ year: new Date().getFullYear(), br: isMobile ? '<br />' : '' }}
          />
        </span>
      </div>
    </footer>
  );
};

export default Footer;